import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, EMPTY, of } from 'rxjs';
import { switchMap, filter, tap, first, map } from 'rxjs/operators';

import { AngularFireAuth } from 'angularfire2/auth';
import { AngularFireDatabase } from 'angularfire2/database';

import { SweetAlertService } from 'src/app/shared/services/sweet-alert/sweet-alert.service';

import { User } from '../models/user.model';

import * as _ from 'lodash';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user: User;
  userRef = this.db.list<User>('usuarios');

  // Callback to set current url before reload the page. Initial page is '/'.
  lastUrl: string = '/';

  constructor(
    private afAuth: AngularFireAuth,
    private db: AngularFireDatabase,
    private router: Router,
    private sweetAlertService: SweetAlertService,
  ) {
    // Set the current url to the property lastUrl.
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        // Case event url was different of the '/sign-in' then save the url path.
        if (e.url !== '/sign-in' && e.url !== '/sign-up' && e.url !== '/reset-password') {
          this.lastUrl = e.url;
        }
      });
  }

  async signIn(email: string, password: string) {
    const result = await this.afAuth.auth.signInWithEmailAndPassword(email, password);

    if (result) {
      // Get user data through result uid user and automatically unsubscribe.
      this.db.object<User>(`usuarios/${result.user.uid}`).snapshotChanges()
        .pipe(first(), tap(data => {
          this.user = { uid: data.payload.key, ...data.payload.val() };

          // console.log('emailVerified', result.user.emailVerified);

          if (this.user.enabled) {
            this.updateUser(this.user);
            this.setAccessToken(result.user.uid);

            // Go to '/' or to last visited url before system logout.
            this.router.navigate([this.lastUrl]);
          } else if (!this.user.enabled && !this.user.currentSignInAt) {
              this.afAuth.auth.signOut()
                .then(() => {
                  this.lastUrl = '/';
                  this.handleLogin(true);
                  this.sweetAlertService.warning({ message: 'Aguarde a liberação do seu acesso pelo administrador do sistema.' });
                })
                .catch(error => this.sweetAlertService.success({ message: this.errorMessage(error.code) }));
          } else {
            this.lastUrl = '/';
            this.handleLogin(true);
            this.sweetAlertService.warning({ message: 'Sua conta está desativada.' });
          }

        })).subscribe();
    }
  }

  async signUp(user: User) {
    return await this.afAuth.auth.createUserWithEmailAndPassword(user.email, user.password)
      .then(data => this.createUser({ uid: data.user.uid, ..._.omit(user, ['password', 'passwordConfirmation']) }));
  }

  resetPassord(email: string) {
    return this.afAuth.auth.sendPasswordResetEmail(email);
  }

  signOut() {
    this.afAuth.auth.signOut()
      .then(() => {
        this.handleLogin(true);
        this.sweetAlertService.success({ message: 'Você efetuou a sua saída do sistema com sucesso.' });
      })
      .catch(error => this.sweetAlertService.success({ message: this.errorMessage(error.code) }));
  }

  getNewUsers() {
    return this.db.list<User>('usuarios',
      ref => ref.orderByChild('currentSignInAt').equalTo(null)).snapshotChanges().pipe(
        map(changes =>
          changes.map(c => ({ uid: c.payload.key, ...c.payload.val() }))
        )
      );
  }

  setAccessToken(accessToken: string) {
    if (accessToken !== null) {
      sessionStorage.setItem('access-token', accessToken);
    }
  }

  getAccessToken() {
    return sessionStorage.getItem('access-token');
  }

  removeAccessToken() {
    sessionStorage.removeItem('access-token');
  }

  get currentUser$(): Observable<User> {
    if (this.getAccessToken() !== null && this.user === undefined) {
      return this.validateAccessToken();
    } else {
      return of(this.user);
    }
  }

  validateAccessToken(): Observable<User> {
    return this.afAuth.authState.pipe(switchMap(user => {
      if (user) {
        this.setAccessToken(user.uid);

        return this.db.object<User>(`usuarios/${user.uid}`).valueChanges()
          .pipe(tap(data => this.user = { uid: user.uid, ...data }));
      } else {
        this.removeAccessToken();

        return EMPTY;
      }
    }));
  }

  isSignedIn(): boolean {
    return this.user !== undefined || this.getAccessToken() !== null;
  }

  signedInUser() {
    return this.afAuth.authState;
  }

  errorMessage(errorCode: string): string {
    const messages = [
      {
        code: 'auth/network-request-failed',
        message: 'Não foi possível processar a sua requisição. Tente novamente mais tarde.'
      },
      {
        code: 'auth/user-not-found',
        message: 'Endereço de e-mail ou senha inválidos ou inexistentes!'
      },
      {
        code: 'auth/wrong-password',
        message: 'Endereço de e-mail ou senha inválidos ou inexistentes!'
      },
      {
        code: 'auth/email-already-in-use',
        message: 'Este e-mail de acesso já está sendo utilizado por outra conta de usuário!'
      },
      {
        code: 'auth/user-disabled',
        message: 'Sua conta de usuário está inativa!'
      },
    ];

    return _.find(messages, ['code', errorCode ]).message;
  }

  handleHome() {
    this.router.navigate(['/']);
  }

  handleLogin(removeAccessUser: boolean = false) {
    this.user = undefined;
    if (removeAccessUser) {
      this.removeAccessToken();
    }
    this.router.navigate(['/sign-in']);
  }

  private createUser(user: User) {
    user.admin = false;
    user.enabled = false;
    user.monitoramentosRealizados = 0;

    const userRef = this.db.object<User>(`usuarios/${user.uid}`);
    return userRef.set(_.omit(user, ['uid']));
  }

  private updateUser(user: User) {
    user.lastSignInAt = user.currentSignInAt || null;
    user.currentSignInAt = moment().format('DD/MM/YYYY - HH:mm');

    return this.userRef.update(user.uid, _.omit(user, ['uid']));
  }
}
